import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertCircle,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  ListChecks,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatCollectionDate } from "@/components/reports/collection-row/format-collection-date";
import { ReportProgressBar } from "@/components/reports/report-item/report-progress-bar";
import type { ReportProgress } from "@/components/reports/report-item/use-report-progress";
import { personInitials, personName } from "@/lib/issues";
import { cn } from "@/lib/utils";
import type {
  CollectionExport,
  ProjectUser,
  ReportCollection,
} from "@/types/api";

/**
 * Column track shared by the header and every row so they line up:
 * name · type (the chip, count included) · generated by · created · chevron.
 *
 * Sized for the narrowest desktop window, 768px less the 80px side rail: the
 * fixed columns are as tight as their contents allow and the gap is smaller
 * below `lg`, so the name keeps about 190px of text there and a long creator
 * name is the one that gives way after it. The type track holds a chip with a
 * four-digit count.
 */
export const COLLECTION_TABLE_GRID =
  "grid grid-cols-[minmax(0,2.4fr)_7.25rem_minmax(0,1.1fr)_5.75rem_1.25rem] items-center gap-3 lg:gap-4";

/**
 * The one bordered, rounded block the whole table sits in, with a hairline
 * between rows. `overflow-clip` rather than `overflow-hidden`: it rounds the
 * corners without becoming a scroll container, so the header can still stick to
 * the top of the scrolling area. See [[collection-table-row.spec]].
 */
export function CollectionTableFrame({ children }: { children: ReactNode }) {
  return (
    <div
      data-slot="collection-table"
      className="divide-y divide-border overflow-clip rounded-[10px] border border-border bg-card"
    >
      {children}
    </div>
  );
}

/** The column titles above the rows. See [[collection-table-row.spec]]. */
export function CollectionTableHeader() {
  return (
    <div
      data-slot="collection-table-header"
      className={cn(
        COLLECTION_TABLE_GRID,
        "sticky top-0 z-10 bg-muted px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
      )}
    >
      <span className="min-w-0 truncate">Name</span>
      <span className="min-w-0 truncate">Type</span>
      <span className="min-w-0 truncate">Generated by</span>
      <span className="min-w-0 truncate">Created</span>
      <span aria-hidden="true" />
    </div>
  );
}

interface CollectionTableRowProps {
  projectId: string;
  collection: ReportCollection;
  /** Photos for a photos report, entries for an issues one. */
  count: number;
  isInProgress: boolean;
  isFailed: boolean;
  progress: ReportProgress;
}

/**
 * One report as a row of columns on a desktop-sized window — the counterpart to
 * the card in [[collection-row.spec]], which decides the export state this only
 * draws. See [[collection-table-row.spec]].
 */
export function CollectionTableRow({
  projectId,
  collection,
  count,
  isInProgress,
  isFailed,
  progress,
}: CollectionTableRowProps) {
  const latest = collection.latest_export;
  const isPhotos = collection.kind === "records";
  const isCompleted = !isInProgress && !isFailed;
  const created = formatCollectionDate(collection.created_at);

  const cells = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <CoverTile
          latest={latest}
          isInProgress={isInProgress}
          isFailed={isFailed}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-medium tracking-[-0.005em]">
            {collection.name}
          </span>
          {isInProgress && <ReportProgressBar progress={progress} />}
          {isFailed && (
            <span className="line-clamp-2 text-xs text-destructive">
              {latest?.error_message || "Last export failed"}
            </span>
          )}
          {collection.deleted_item_count > 0 && (
            <span className="truncate text-xs text-muted-foreground">
              {collection.deleted_item_count} removed from the project
            </span>
          )}
        </div>
      </div>

      <div className="min-w-0">
        {/* Kind and count in one chip. A light tint of the kind's colour, the
            icon in that colour, and the word — so colour is never the only cue
            — with the count bold so a column of chips reads down its numbers.
            The text stays the ordinary text colour on the tint. */}
        <Badge
          variant="outline"
          className={cn(
            "gap-1 border-transparent",
            isPhotos ? "bg-kind-photos/15" : "bg-kind-issues/15",
          )}
        >
          {isPhotos ? (
            <ImageIcon
              data-slot="kind-icon"
              aria-hidden="true"
              className="size-3 text-kind-photos"
            />
          ) : (
            <ListChecks
              data-slot="kind-icon"
              aria-hidden="true"
              className="size-3 text-kind-issues"
            />
          )}
          <span>
            <span className="font-bold tabular-nums">{count}</span>{" "}
            {kindWord(isPhotos, count)}
          </span>
        </Badge>
      </div>

      <div className="min-w-0">
        <CreatorCell person={collection.created_by} />
      </div>

      <span
        className={cn(
          "min-w-0 truncate text-sm",
          !created && "text-muted-foreground/70",
        )}
      >
        {created ?? "—"}
      </span>

      <div className="flex items-center justify-end">
        {/* Only a report that opens gets the arrow: an exporting or failed row
            is not a link, and an arrow on it would promise a click that does
            nothing. */}
        {isCompleted && (
          <ChevronRight
            data-slot="open-chevron"
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </div>
    </>
  );

  // The card fill is on the row itself, as on the Issues table's rows, so the
  // hover and press fills below replace it rather than sit over it.
  const rowClassName = cn(COLLECTION_TABLE_GRID, "min-h-16 bg-card px-3 py-2");

  return (
    <div
      data-slot="collection-row"
      data-layout="table"
      className={cn(
        "relative bg-card",
        // A bar drawn over the edge, not a border: a border would push this
        // row's columns 2px out of line with the header and the rows around it.
        isFailed &&
          "before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-destructive",
        isInProgress && "pointer-events-none opacity-80",
      )}
    >
      {/* Only a settled report is worth opening: a running export has no
          versions to show yet, and a failed one has nothing at all. */}
      {isCompleted ? (
        <Link
          to="/projects/$projectId/reports/$reportId"
          params={{ projectId, reportId: collection.id }}
          className={cn(
            rowClassName,
            "transition-colors duration-100 hover:bg-card-hover active:bg-card-pressed",
          )}
        >
          {cells}
        </Link>
      ) : (
        <div className={rowClassName}>{cells}</div>
      )}
    </div>
  );
}

/** The chip's word, singular for exactly one: `1 Photo`, `58 Issues`. */
function kindWord(isPhotos: boolean, count: number): string {
  if (isPhotos) return count === 1 ? "Photo" : "Photos";
  return count === 1 ? "Issue" : "Issues";
}

function CoverTile({
  latest,
  isInProgress,
  isFailed,
}: {
  latest: CollectionExport | null;
  isInProgress: boolean;
  isFailed: boolean;
}) {
  const thumbnailSrc = latest?.thumbnail ?? null;
  const isCompleted = !isInProgress && !isFailed;

  return (
    <div className="relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
      {isInProgress && <FileText className="h-5 w-5 text-muted-foreground" />}
      {isFailed && <AlertCircle className="h-5 w-5 text-destructive" />}
      {isCompleted && thumbnailSrc && (
        <img
          src={thumbnailSrc}
          alt=""
          crossOrigin="anonymous"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* No cover — the same generic glyph the card shows, not a format's. */}
      {isCompleted && !thumbnailSrc && (
        <FileText className="h-5 w-5 text-muted-foreground" />
      )}
    </div>
  );
}

function CreatorCell({ person }: { person: ProjectUser | null }) {
  if (!person) {
    return (
      <span className="block truncate text-sm text-muted-foreground/70">
        Unknown user
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tertiary text-[10px] font-semibold text-tertiary-foreground"
        aria-hidden="true"
      >
        {personInitials(person)}
      </span>
      <span className="truncate text-sm">{personName(person)}</span>
    </span>
  );
}
